import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaCog, FaCommentDots, FaCopy, FaDesktop, FaDownload, FaEye, FaMobileAlt, FaSms, FaTimes, FaUser } from 'react-icons/fa';
import Header from '../Header';
import { useNotification } from '../components/NotificationProvider';
import api from '../services/apiService';
import {
  ACADEMY_ADMIN_API,
  ACADEMY_ROLE_TREASURER,
  academiesForRole,
  academySettingsPathFor,
  getCachedAcademies,
  setCachedAcademies,
} from '../utils/academyAccess';
import { compareAcademyTeams } from '../utils/academyTeamSort';
import { authService } from '../services/authService';
import '../scss/membershipPlayers.scss';

const formatGbp = (pence) => {
  const value = Number(pence) || 0;
  return `£${(value / 100).toFixed(2)}`;
};

const currentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const percentLabel = (part, whole) => {
  const total = Number(whole) || 0;
  if (total < 1) {
    return '0%';
  }
  return `${Math.round(((Number(part) || 0) / total) * 100)}%`;
};

const AccessIcons = ({ hasApp, hasDesktop, name }) => (
  <span className="treasurer-access">
    <FaMobileAlt
      className={`treasurer-app-icon${hasApp ? ' is-on-app' : ' is-off-app'}`}
      aria-label={hasApp ? `${name} has the app` : `${name} does not have the app`}
    />
    <FaDesktop
      className={`treasurer-app-icon${hasDesktop ? ' is-on-app' : ' is-off-app'}`}
      aria-label={hasDesktop ? `${name} uses desktop` : `${name} does not use desktop`}
    />
  </span>
);

const TreasurerBentos = ({ last7, monthToDate, paid, outstanding, due }) => {
  const cards = [
    { label: 'Collected last 7 days', amount: last7, whole: due },
    { label: 'Collected since start of month', amount: monthToDate, whole: due },
    { label: 'Total paid', amount: paid, whole: due },
    { label: 'Total outstanding', amount: outstanding, whole: due },
  ];
  return (
    <div className="treasurer-bentos">
      {cards.map((card) => (
        <div key={card.label}>
          <span>{card.label}</span>
          <strong>{percentLabel(card.amount, card.whole)}</strong>
          <small>{formatGbp(card.amount)}</small>
        </div>
      ))}
    </div>
  );
};

const feeDeducted = (row) => {
  if (row.kind !== 'completed' || row.amount_pence == null || row.net_pence == null) {
    return '—';
  }
  return formatGbp(Math.max(0, Number(row.amount_pence) - Number(row.net_pence)));
};

const ActivityTable = ({
  activity,
  loading,
  error,
  month,
  kind,
  onMonth,
  onKind,
  onPage,
}) => {
  const rows = activity?.rows || [];
  const total = Number(activity?.total) || 0;
  const page = Number(activity?.page) || 1;
  const perPage = Number(activity?.per_page) || 25;
  const from = total === 0 ? 0 : ((page - 1) * perPage) + 1;
  const to = Math.min(page * perPage, total);
  const months = activity?.months || [];
  return (
    <section className={`treasurer-activity${loading ? ' is-loading' : ''}`} aria-busy={loading}>
      <div className="treasurer-filters">
        <label>
          Month
          <select value={month} disabled={loading} onChange={(event) => onMonth(event.target.value)}>
            <option value="">All months</option>
            {months.map((item) => (
              <option key={item.value} value={item.value}>{item.label || item.value}</option>
            ))}
          </select>
        </label>
        <label>
          Activity
          <select value={kind} disabled={loading} onChange={(event) => onKind(event.target.value)}>
            <option value="all">All</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="not_processed">Not completed</option>
          </select>
        </label>
      </div>
      {error ? <div className="error">{error}</div> : null}
      {loading ? <div className="treasurer-activity-loading">Loading activity…</div> : null}
      {activity?.stripe_error && rows.length > 0 ? <div className="error">{activity.stripe_error}</div> : null}
      {!error && activity && rows.length === 0 ? (
        <div className="membership-empty">{activity.stripe_error || 'No payment activity for this filter.'}</div>
      ) : null}
      {rows.length > 0 ? (
        <div className="treasurer-table-wrap">
          <table className="treasurer-table treasurer-activity-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Activity</th>
                <th>Parent</th>
                <th>Child</th>
                <th>Team</th>
                <th>Month</th>
                <th>Amount</th>
                <th>Fees deducted</th>
                <th>Reached account</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={loading ? 'is-loading' : ''}>
                  <td>{row.occurred_label || '—'}</td>
                  <td className={`treasurer-activity-kind is-${row.kind}`}>{row.label}</td>
                  <td>{row.parent_name || '—'}</td>
                  <td>{row.player_name || '—'}</td>
                  <td>{row.team_name || '—'}</td>
                  <td>{row.month_label || row.year_month || '—'}</td>
                  <td>{row.amount_pence == null ? '—' : formatGbp(row.amount_pence)}</td>
                  <td>{feeDeducted(row)}</td>
                  <td>{row.kind === 'completed' && row.net_pence != null ? formatGbp(row.net_pence) : '—'}</td>
                  <td>{row.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {total > perPage ? (
        <div className="treasurer-pager">
          <span>Showing {from}–{to} of {total}</span>
          <button type="button" disabled={page < 2 || loading} onClick={() => onPage(page - 1)}>Previous</button>
          <button type="button" disabled={to >= total || loading} onClick={() => onPage(page + 1)}>Next</button>
        </div>
      ) : total > 0 ? (
        <div className="treasurer-pager">
          <span>Showing {from}–{to} of {total}</span>
        </div>
      ) : null}
    </section>
  );
};

const compareParentRows = (a, b) => {
  const rank = (row) => (row.isFree || row.paid ? 0 : 1);
  const byRank = rank(a) - rank(b);
  if (byRank !== 0) {
    return byRank;
  }
  const byPlayer = a.playerName.localeCompare(b.playerName, 'en', { sensitivity: 'base' });
  if (byPlayer !== 0) {
    return byPlayer;
  }
  return a.parentName.localeCompare(b.parentName, 'en', { sensitivity: 'base' });
};

const compareTeamNames = (a, b) => String(a.team_name || '').localeCompare(
  String(b.team_name || ''),
  'en',
  { sensitivity: 'base', numeric: true }
);

const teamLabel = (team) => {
  const name = team.team_name || `Team ${team.team_id}`;
  const age = String(team.age_group || '').trim();
  if (age && !name.toLowerCase().includes(age.toLowerCase())) {
    return `${name} · ${age}`;
  }
  return name;
};

const treasurerAcademies = (list) => (
  authService.isSuperAdmin() ? (list || []) : academiesForRole(list, ACADEMY_ROLE_TREASURER)
);

const NOTICE_LIMIT = 120;
const TITLE_LIMIT = 40;
// Twilio sends 160 GSM-7 characters per text, or 70 when any character needs Unicode.
const SMS_LIMIT = 160;
const SMS_UNICODE_LIMIT = 70;
const GSM_CHARS = new Set(Array.from("@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"));
const SMS_REPLACEMENTS = {
  '‘': "'", '’': "'", '“': '"', '”': '"', '–': '-', '—': '-', '…': '...', '\u00A0': ' ',
};

const cleanSmsText = (text) => Array.from(String(text || ''))
  .map((char) => SMS_REPLACEMENTS[char] ?? char)
  .join('')
  .replace(/\s+/g, ' ')
  .trim();

const smsLimitFor = (text) => (
  Array.from(text).every((char) => GSM_CHARS.has(char)) ? SMS_LIMIT : SMS_UNICODE_LIMIT
);

const suggestedNoticeTitle = (academyName) => {
  const words = String(academyName || '').trim().split(/\s+/).filter(Boolean);
  const initials = words.map((word) => {
    const clean = word.replace(/[^A-Za-z0-9]/g, '');
    if (!clean) {
      return '';
    }
    if (clean.length <= 3 && clean === clean.toUpperCase()) {
      return clean;
    }
    return clean.charAt(0).toUpperCase();
  }).join('');
  const label = initials ? `${initials} Membership` : 'Membership';
  return label.slice(0, TITLE_LIMIT);
};

const signedInRecipient = () => {
  const name = [sessionStorage.getItem('first_name'), sessionStorage.getItem('last_name')]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  return {
    kind: 'self',
    id: Number(sessionStorage.getItem('user_id')) || 0,
    name: name || 'You',
    role: 'You',
    detail: 'Your device',
    is_self: true,
  };
};

const EXTENDED_FAMILY = new Set([
  'grandmother', 'grandfather', 'grandparent', 'grandma', 'grandpa',
  'aunt', 'uncle', 'cousin', 'sibling', 'brother', 'sister', 'relative',
]);

const GUARDIAN_ROLES = new Set([
  'mother', 'father', 'mum', 'dad', 'mom',
  'step-mother', 'step-father', 'stepmother', 'stepfather', 'step mother', 'step father',
  'guardian', 'parent', 'carer', 'legal guardian',
]);

const noticeRoleLabel = (role) => {
  const value = String(role || '').trim();
  const lower = value.toLowerCase();
  if (!value || lower === 'other' || lower === 'primary' || lower === 'secondary') {
    return 'Parent';
  }
  return value;
};

const keepNoticeGuardian = (guardian) => {
  const status = String(guardian?.approval_status || '').trim().toLowerCase();
  if (status === 'rejected') {
    return false;
  }
  const role = String(guardian?.role || '').trim().toLowerCase();
  if (EXTENDED_FAMILY.has(role)) {
    return false;
  }
  if (GUARDIAN_ROLES.has(role)) {
    return true;
  }
  const type = String(guardian?.relationship_type || '').trim().toLowerCase();
  const legal = guardian?.is_legal_guardian === true || guardian?.is_legal_guardian === 1;
  if (role === 'primary' || role === 'parent' || role === '' || type === 'primary' || legal) {
    return true;
  }
  return false;
};

const parentStillNeedsSetup = (row) => {
  if (row.isFree || row.paid) {
    return false;
  }
  const kind = row.subscriptionKind || '';
  return kind !== 'active' && kind !== 'failed';
};

const parentAudienceFromRows = (rows, unpaidOnly) => {
  const people = new Map();
  rows.forEach((row) => {
    if (unpaidOnly && !parentStillNeedsSetup(row)) {
      return;
    }
    const guardians = row.guardians?.length
      ? row.guardians
      : (row.parentName && row.parentName !== 'No parent'
        ? [{ name: row.parentName, role: 'Parent' }]
        : []);
    guardians.forEach((guardian) => {
      if (!keepNoticeGuardian(guardian)) {
        return;
      }
      const name = String(guardian.name || '').trim();
      if (!name) {
        return;
      }
      const key = guardian.parent_id ? `id-${guardian.parent_id}` : `name-${name.toLowerCase()}`;
      const current = people.get(key) || { name, role: '', children: '', links: [] };
      const role = noticeRoleLabel(guardian.role);
      const child = String(row.playerName || '').trim();
      if (!current.links.some((link) => link.role === role && link.child === child)) {
        current.links.push({ role, child });
      }
      people.set(key, current);
    });
  });
  return [...people.values()].map((person) => {
    const roles = [];
    const children = [];
    person.links.forEach((link) => {
      if (link.role && !roles.includes(link.role)) {
        roles.push(link.role);
      }
      if (link.child && !children.includes(link.child)) {
        children.push(link.child);
      }
    });
    return {
      ...person,
      role: roles.join(', ') || 'Parent',
      children: children.join(', '),
    };
  }).sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
};

const SendNotificationForm = ({ academyId, academyName, month, parents, parentsReady, showSuccess, showError }) => {
  const suggestedTitle = suggestedNoticeTitle(academyName);
  const initialRecipient = signedInRecipient();
  const [audience, setAudience] = useState('parents');
  const [unpaidOnly, setUnpaidOnly] = useState(true);
  const [title, setTitle] = useState(suggestedTitle);
  const [titleEdited, setTitleEdited] = useState(false);
  const [message, setMessage] = useState('');
  const [fullMessage, setFullMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [signedIn, setSignedIn] = useState(initialRecipient);
  const [recipient, setRecipient] = useState(initialRecipient);
  const [recipientQuery, setRecipientQuery] = useState(initialRecipient.name);
  const [recipientEdited, setRecipientEdited] = useState(false);
  const [recipientResults, setRecipientResults] = useState([]);
  const [recipientSearching, setRecipientSearching] = useState(false);
  const [recipientSearchError, setRecipientSearchError] = useState(false);
  const [sends, setSends] = useState([]);
  const [feedError, setFeedError] = useState(false);
  const [confirmRecent, setConfirmRecent] = useState(false);
  const [recentWarning, setRecentWarning] = useState('');
  const [sentDetail, setSentDetail] = useState(null);
  const [smsFallback, setSmsFallback] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const smsText = cleanSmsText(smsMessage);
  const smsLength = Array.from(smsText).length;
  const smsLimit = smsLimitFor(smsText);
  const isSuperAdmin = authService.isSuperAdmin();
  const [serverAudience, setServerAudience] = useState(null);
  const [audienceError, setAudienceError] = useState('');
  const recipientEditedRef = useRef(false);
  const parentRows = parents || [];
  const audiencePlayerIds = useMemo(() => {
    if (audience !== 'parents') {
      return '';
    }
    return parentRows
      .filter((row) => (unpaidOnly ? parentStillNeedsSetup(row) : true))
      .map((row) => row.playerId)
      .filter(Boolean)
      .join(',');
  }, [audience, unpaidOnly, parentRows]);
  const clientAudience = useMemo(
    () => (audience === 'parents' ? parentAudienceFromRows(parentRows, unpaidOnly) : []),
    [audience, unpaidOnly, parentRows]
  );

  useEffect(() => {
    if (!titleEdited) {
      setTitle(suggestedTitle);
    }
  }, [suggestedTitle, titleEdited]);

  useEffect(() => {
    let cancelled = false;
    const loadSignedIn = async () => {
      try {
        const response = await api.get(
          `${ACADEMY_ADMIN_API}/send_treasurer_notification.php?id=${encodeURIComponent(academyId)}&me=1`
        );
        const me = response.data?.me;
        if (cancelled || !me?.name) {
          return;
        }
        const person = { ...me, kind: 'self', is_self: true, role: 'You' };
        setSignedIn(person);
        if (!recipientEditedRef.current) {
          setRecipient(person);
          setRecipientQuery(person.name);
        }
      } catch (err) {
        // The signed-in name from this browser stays in the field.
      }
    };
    loadSignedIn();
    return () => {
      cancelled = true;
    };
  }, [academyId]);

  useEffect(() => {
    if (audience === 'parents' && !parentsReady) {
      return undefined;
    }
    let cancelled = false;
    const loadAudience = async () => {
      setAudienceError('');
      setServerAudience(null);
      try {
        const params = new URLSearchParams({
          id: String(academyId),
          audience_list: '1',
          audience,
        });
        if (audience === 'parents') {
          params.set('players', audiencePlayerIds);
        }
        const response = await api.get(
          `${ACADEMY_ADMIN_API}/send_treasurer_notification.php?${params.toString()}`
        );
        if (cancelled) {
          return;
        }
        if (response.data?.audience) {
          setServerAudience(Array.isArray(response.data.people) ? response.data.people : []);
          return;
        }
        if (audience === 'coaches') {
          setAudienceError('The coach list could not be loaded.');
        }
      } catch (err) {
        if (!cancelled && audience === 'coaches') {
          setAudienceError('The coach list could not be loaded.');
        }
      }
    };
    loadAudience();
    return () => {
      cancelled = true;
    };
  }, [academyId, audience, audiencePlayerIds, parentsReady]);

  useEffect(() => {
    if (!recipientEdited) {
      setRecipientResults([]);
      setRecipientSearching(false);
      setRecipientSearchError(false);
      return undefined;
    }
    const term = recipientQuery.trim();
    if (term.length < 2 || (recipient && term === recipient.name)) {
      setRecipientResults([]);
      setRecipientSearching(false);
      setRecipientSearchError(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setRecipientSearching(true);
      try {
        const response = await api.get(
          `${ACADEMY_ADMIN_API}/send_treasurer_notification.php?id=${encodeURIComponent(academyId)}&term=${encodeURIComponent(term)}`
        );
        if (!cancelled) {
          setRecipientSearchError(false);
          setRecipientResults(response.data?.people || []);
        }
      } catch (err) {
        if (!cancelled) {
          setRecipientResults([]);
          setRecipientSearchError(true);
        }
      } finally {
        if (!cancelled) {
          setRecipientSearching(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [academyId, recipient, recipientEdited, recipientQuery]);

  const loadFeed = useCallback(async () => {
    try {
      const response = await api.get(
        `${ACADEMY_ADMIN_API}/send_treasurer_notification.php?id=${encodeURIComponent(academyId)}&feed=1`
      );
      setSends(response.data?.sends || []);
      setFeedError(false);
    } catch (err) {
      setFeedError(true);
    }
  }, [academyId]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const audienceLabel = unpaidOnly ? 'Parents who have not set up' : 'All parents';

  const deliver = async (preview) => {
    const noticeTitle = title.trim();
    const text = message.trim();
    if (!noticeTitle) {
      showError('Write a short title before sending.');
      return;
    }
    if (noticeTitle.length > TITLE_LIMIT) {
      showError(`Keep the title to ${TITLE_LIMIT} characters.`);
      return;
    }
    if (!text) {
      showError('Write a message before sending.');
      return;
    }
    if (text.length > NOTICE_LIMIT) {
      showError(`Keep the message to ${NOTICE_LIMIT} characters so it fits a push notification.`);
      return;
    }
    const useSms = isSuperAdmin && smsFallback;
    if (useSms && !smsText) {
      showError('Write the SMS fallback message before sending.');
      return;
    }
    if (useSms && smsLength > smsLimit) {
      showError(`Keep the SMS to ${smsLimit} characters so it sends as one text.`);
      return;
    }
    const testPerson = preview === 'test' ? recipient : null;
    if (preview === 'test' && !testPerson) {
      showError('Choose a parent or coach, or send the test to yourself.');
      return;
    }
    if (!preview && !confirmRecent && sends.some((row) => row.recent)) {
      const recent = sends.find((row) => row.recent);
      const who = recent?.sent_by_name || 'a treasurer';
      setRecentWarning(`A manual notification was sent on ${recent?.created_label || 'a recent day'} by ${who}. Sending another within 7 days may be too soon.`);
      setConfirmRecent(true);
      return;
    }
    if (preview === true) {
      setPreviewing(true);
    } else if (preview === 'test') {
      setTesting(true);
    } else {
      setSending(true);
    }
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/send_treasurer_notification.php`, {
        id: Number(academyId),
        audience,
        unpaid_only: audience === 'parents' && unpaidOnly,
        month: month || '',
        title: noticeTitle,
        message: text,
        full_message: fullMessage.trim(),
        sms_fallback: useSms,
        sms_message: useSms ? smsText : '',
        preview: preview === true,
        confirm_recent: !preview && confirmRecent,
        ...(preview === 'test' ? {
          recipient_kind: testPerson.kind,
          recipient_id: testPerson.id,
        } : {}),
      });
      const data = response.data || {};
      if (data.needs_confirm) {
        setRecentWarning(data.message || 'A manual notification was sent in the last 7 days.');
        setConfirmRecent(true);
        return;
      }
      if (!data.success) {
        throw new Error(data.message || 'The notification could not be sent.');
      }
      showSuccess(data.message || (preview ? 'Test sent.' : 'Notification sent.'));
      if (!preview) {
        setMessage('');
        setFullMessage('');
        setSmsMessage('');
        setConfirmRecent(false);
        setRecentWarning('');
      }
      loadFeed();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'The notification could not be sent.');
    } finally {
      setPreviewing(false);
      setTesting(false);
      setSending(false);
    }
  };

  const sendingTestToSelf = Boolean(recipient && (recipient.is_self || recipient.kind === 'self'));
  const busy = sending || previewing || testing;
  const audiencePeople = serverAudience || (audience === 'parents' ? clientAudience : []);
  const audienceTitle = audience === 'coaches' ? 'Coaches' : 'Parents';
  const waitingForAudience = audience === 'coaches' && serverAudience === null && !audienceError;
  const audienceCountLabel = audience === 'coaches'
    ? (audiencePeople.length === 1 ? '1 coach' : `${audiencePeople.length} coaches`)
    : (audiencePeople.length === 1 ? '1 parent' : `${audiencePeople.length} parents`);
  const audienceRows = useMemo(() => {
    if (audience === 'coaches') {
      return audiencePeople.map((person) => ({
        player: person.name,
        parents: [{ name: person.role || 'Coach', role: '' }],
      }));
    }
    const byPlayer = new Map();
    audiencePeople.forEach((person) => {
      const links = person.links?.length
        ? person.links
        : String(person.children || '').split(',').map((child) => ({
          role: person.role || 'Parent',
          child: child.trim(),
        })).filter((link) => link.child);
      links.forEach((link) => {
        const player = String(link.child || '').trim() || 'Player';
        const row = byPlayer.get(player) || { player, parents: [] };
        const role = link.role || 'Parent';
        if (!row.parents.some((parent) => parent.name === person.name && parent.role === role)) {
          row.parents.push({ name: person.name, role });
        }
        byPlayer.set(player, row);
      });
    });
    return [...byPlayer.values()].sort((a, b) => a.player.localeCompare(b.player, 'en', { sensitivity: 'base' }));
  }, [audience, audiencePeople]);
  const deliveryEstimate = useMemo(() => {
    if (audience !== 'parents' || !serverAudience || !serverAudience.some((person) => typeof person.push_on === 'boolean')) {
      return null;
    }
    const withPhone = serverAudience.filter((person) => person.has_phone !== false);
    const push = withPhone.filter((person) => person.push_on === true).length;
    return {
      push,
      sms: withPhone.length - push,
      noPhone: serverAudience.length - withPhone.length,
    };
  }, [audience, serverAudience]);
  const sendLabel = confirmRecent
    ? `Send anyway to ${audienceTitle}`
    : `Send notification to ${audienceTitle}`;

  const chooseRecipient = (person) => {
    recipientEditedRef.current = true;
    setRecipientEdited(true);
    setRecipient(person);
    setRecipientQuery(person.name);
    setRecipientResults([]);
  };

  const useSignedIn = () => {
    recipientEditedRef.current = false;
    setRecipientEdited(false);
    setRecipient(signedIn);
    setRecipientQuery(signedIn.name);
    setRecipientResults([]);
  };

  const sendTest = () => {
    if (!recipient) {
      showError('Choose a parent or coach, or send the test to yourself.');
      return;
    }
    if (sendingTestToSelf) {
      deliver(true);
      return;
    }
    deliver('test');
  };

  const onSubmit = (event) => {
    event.preventDefault();
    deliver(false);
  };

  const openSent = async (row) => {
    setSentDetail({ ...row, loading: true, people: [], error: '' });
    try {
      const response = await api.get(
        `${ACADEMY_ADMIN_API}/send_treasurer_notification.php?id=${encodeURIComponent(academyId)}&send_id=${encodeURIComponent(row.id)}`
      );
      setSentDetail({
        ...row,
        loading: false,
        people: response.data?.people || [],
        error: '',
      });
    } catch (err) {
      setSentDetail({
        ...row,
        loading: false,
        people: [],
        error: 'The list of people could not be loaded.',
      });
    }
  };

  const sentPeople = sentDetail?.people || [];
  const sentTotal = sentPeople.length;
  const sentSeen = sentPeople.filter((person) => person.seen).length;
  const sentPushKnown = sentPeople.some((person) => typeof person.push_on === 'boolean');
  const sentPushOn = sentPeople.filter((person) => person.push_on === true).length;
  const sentNoun = sentPeople.every((person) => person.role === 'Coach')
    ? (sentTotal === 1 ? 'coach' : 'coaches')
    : (sentTotal === 1 ? 'parent' : 'parents');

  const feedKind = (kind) => {
    if (kind === 'automatic') {
      return 'Automatic';
    }
    if (kind === 'test') {
      return 'Test';
    }
    return 'Manual';
  };

  return (
    <div className="treasurer-notify-layout">
    <form className="treasurer-notice" onSubmit={onSubmit}>
      <h2 className="treasurer-column-title">Write notification</h2>
      <label>
        Audience
        <select value={audience} onChange={(event) => setAudience(event.target.value)}>
          <option value="parents">Parents</option>
          <option value="coaches">Coaches</option>
        </select>
      </label>

      {audience === 'parents' && (
        <div className="treasurer-notice-toggle">
          <button
            type="button"
            className={`treasurer-switch${unpaidOnly ? ' is-on' : ''}`}
            role="switch"
            aria-checked={unpaidOnly}
            aria-label={audienceLabel}
            onClick={() => setUnpaidOnly((value) => !value)}
          >
            <span className="track" aria-hidden="true"><span className="knob" /></span>
          </button>
          <span>{audienceLabel}</span>
        </div>
      )}

      <label>
        Title
        <input
          type="text"
          value={title}
          maxLength={TITLE_LIMIT}
          placeholder={suggestedTitle}
          onChange={(event) => {
            setTitleEdited(true);
            setTitle(event.target.value.slice(0, TITLE_LIMIT));
          }}
        />
      </label>
      <div className="treasurer-notice-count">{title.trim().length}/{TITLE_LIMIT}</div>

      <label>
        Push message
        <textarea
          value={message}
          maxLength={NOTICE_LIMIT}
          rows={3}
          placeholder="Short message for the phone"
          onChange={(event) => setMessage(event.target.value.slice(0, NOTICE_LIMIT))}
        />
      </label>
      <div className="treasurer-notice-count">{message.trim().length}/{NOTICE_LIMIT}</div>

      <label>
        Full notification
        <textarea
          value={fullMessage}
          rows={6}
          placeholder="Longer message shown in the app. Leave this blank to use the push message."
          onChange={(event) => setFullMessage(event.target.value)}
        />
      </label>

      {isSuperAdmin ? (
        <div className="treasurer-notice-box">
          <h3 className="treasurer-notice-test-title">SMS fallback</h3>
          <p className="treasurer-notice-hint">
            Sent by Twilio when a push cannot be delivered, usually because the app is not installed.
          </p>
          <label className="treasurer-notice-sms">
            <input
              type="checkbox"
              checked={smsFallback}
              onChange={(event) => setSmsFallback(event.target.checked)}
            />
            <span>Send via SMS fallback</span>
          </label>
          {smsFallback ? (
            <>
              <label>
                SMS message
                <textarea
                  value={smsMessage}
                  rows={3}
                  placeholder="Short text message. The title is not added."
                  onChange={(event) => setSmsMessage(event.target.value)}
                />
              </label>
              <div className={`treasurer-notice-count${smsLength > smsLimit ? ' is-over' : ''}`}>
                {smsLength}/{smsLimit}
              </div>
              {smsLimit < SMS_LIMIT ? (
                <p className="treasurer-notice-hint">
                  {`This message has a special character (such as emoji or €), so one text only fits ${SMS_UNICODE_LIMIT} characters.`}
                </p>
              ) : null}
            </>
          ) : null}
          {deliveryEstimate ? (
            <p className="treasurer-notice-hint">
              {`${deliveryEstimate.push} ${deliveryEstimate.push === 1 ? 'parent has' : 'parents have'} the app and should get a push. `}
              {smsFallback
                ? `About ${deliveryEstimate.sms} would get an SMS instead.`
                : `${deliveryEstimate.sms} ${deliveryEstimate.sms === 1 ? 'has' : 'have'} no app and would get nothing unless SMS fallback is on.`}
              {deliveryEstimate.noPhone > 0
                ? ` ${deliveryEstimate.noPhone} ${deliveryEstimate.noPhone === 1 ? 'has' : 'have'} no phone number.`
                : ''}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="treasurer-notice-test">
        <h3 className="treasurer-notice-test-title">Send a test</h3>
        <p className="treasurer-notice-hint">Send a test to yourself or another individual before sending to all.</p>
        <label>
          Send test to
          <input
            type="search"
            value={recipientQuery}
            placeholder="Search for a parent or coach"
            onChange={(event) => {
              const value = event.target.value;
              recipientEditedRef.current = true;
              setRecipientEdited(true);
              setRecipientQuery(value);
              if (value.trim() !== (recipient?.name || '')) {
                setRecipient(null);
              }
            }}
          />
        </label>
        <p className="treasurer-notice-hint">
          {recipient
            ? `This test goes to ${recipient.name}${sendingTestToSelf ? ' (you)' : recipient.role ? ` (${recipient.role})` : ''} only.`
            : recipientSearching
              ? 'Searching…'
              : 'Search for a parent or coach on this academy.'}
          {!sendingTestToSelf && (
            <>
              {' '}
              <button type="button" className="treasurer-notice-reset" onClick={useSignedIn}>
                Use me
              </button>
            </>
          )}
        </p>
        {recipientEdited && recipientQuery.trim().length >= 2 && !recipientSearching && !recipient && (recipientSearchError || recipientResults.length === 0) && (
          <p className="treasurer-notice-hint">
            {recipientSearchError ? 'Search could not be loaded.' : 'No parent or coach matches.'}
          </p>
        )}
        {recipientResults.length > 0 && (
          <ul className="treasurer-notice-results">
            {recipientResults.map((person) => (
              <li key={`${person.kind}-${person.id}`}>
                <button type="button" onClick={() => chooseRecipient(person)}>
                  {person.name}
                  <small>{[person.role, person.detail].filter(Boolean).join(' · ')}</small>
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="treasurer-notice-preview"
          disabled={busy}
          onClick={sendTest}
        >
          {previewing || testing ? 'Sending test…' : sendingTestToSelf ? 'Send preview' : 'Send test'}
        </button>
      </div>

      <hr className="treasurer-notice-rule" />
      <div className="treasurer-notice-publish">
        <p className="treasurer-notice-check">Everything Ok?</p>
        {recentWarning && (
          <p className="treasurer-notice-warning">{recentWarning}</p>
        )}
        <button
          type="submit"
          className="treasurer-notice-send"
          disabled={busy || waitingForAudience || !!audienceError || audiencePeople.length === 0}
        >
          {sending ? 'Sending…' : sendLabel}
        </button>
        <div className="treasurer-notice-follow">
        {audiencePeople.length > 0 ? (
          <details className="treasurer-notice-audience" key={`${audience}-${unpaidOnly}`}>
            <summary>
              <span>{`This will be sent to ${audienceCountLabel}`}</span>
              <span className="treasurer-notice-audience-more" />
            </summary>
            <ul>
              {audienceRows.map((row, index) => (
                <li key={`${row.player}-${index}`}>
                  <strong>{row.player}</strong>
                  <span className="treasurer-notice-audience-parents">
                    {row.parents.map((parent) => (
                      <span key={`${parent.name}-${parent.role}`}>
                        {parent.name}
                        {parent.role ? <small>{parent.role}</small> : null}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        ) : (
          <p className="treasurer-notice-audience-status">
            {audienceError
              ? 'The audience list is not available yet.'
              : waitingForAudience
                ? 'Checking who will receive this…'
                : audience === 'coaches'
                  ? 'No coaches are assigned to this academy’s teams.'
                  : 'No parents or guardians will receive this.'}
          </p>
        )}
        {audienceError ? <p className="treasurer-notice-hint">{audienceError}</p> : null}
        <p className="treasurer-notice-hint">
          {audience === 'coaches'
            ? 'This goes to the coaches assigned to this academy’s teams.'
            : unpaidOnly
              ? 'Free players and parents who already have a subscription are left out. A parent with another child still to set up is included once.'
              : 'This is sent only to the parent or guardian set up on the player. Grandparents, aunts, uncles and other relatives are left out unless they are that parent or guardian. The role shown is the one linked to the child.'}
        </p>
        </div>
      </div>
    </form>
    <aside className="treasurer-notice-feed" aria-label="Notifications sent">
      <h2 className="treasurer-column-title">Sent</h2>
      {feedError && <p className="treasurer-notice-hint">Recent notifications could not be loaded.</p>}
      {!feedError && sends.length === 0 && <p className="treasurer-notice-hint">Nothing has been sent yet.</p>}
      <ul>
        {sends.map((row) => (
          <li key={row.id}>
            <button type="button" className="treasurer-notice-sent" onClick={() => openSent(row)}>
              <span className={`treasurer-notice-kind is-${row.kind}`}>{feedKind(row.kind)}</span>
              <strong>{row.title}</strong>
              <p>{row.message}</p>
              <small>
                {row.created_label}
                {row.kind === 'automatic' ? ` · ${row.recipient_label}` : ''}
                {row.kind === 'manual' ? ` · ${row.recipient_label} · ${row.sent_by_name || 'Treasurer'}` : ''}
                {row.kind === 'test' ? ` · Test to ${row.recipient_label} · ${row.sent_by_name || 'Treasurer'}` : ''}
                {row.recipient_count > 1 ? ` · ${row.recipient_count} people` : ''}
              </small>
            </button>
          </li>
        ))}
      </ul>
    </aside>
    {sentDetail ? (
      <div className="modal-overlay" onClick={() => setSentDetail(null)}>
        <div
          className="modal treasurer-parents-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="treasurer-sent-title"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="treasurer-modal-close"
            onClick={() => setSentDetail(null)}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          <h2 id="treasurer-sent-title">{sentDetail.title || 'Notification'}</h2>
          <p className="treasurer-notice-hint">
            {`Sent: ${feedKind(sentDetail.kind)}${sentDetail.created_label ? ` · ${sentDetail.created_label}` : ''}`}
          </p>
          {!sentDetail.loading && !sentDetail.error && sentTotal > 0 ? (
            <div className="treasurer-sent-bentos">
              <div>
                <span>Seen</span>
                <strong>{sentSeen}</strong>
                <small>{`${percentLabel(sentSeen, sentTotal)} of ${sentTotal} ${sentNoun}`}</small>
              </div>
              {sentPushKnown ? (
                <div>
                  <span>Notifications on</span>
                  <strong>{sentPushOn}</strong>
                  <small>{`${percentLabel(sentPushOn, sentTotal)} of ${sentTotal} ${sentNoun}`}</small>
                </div>
              ) : null}
            </div>
          ) : null}
          {sentDetail.loading ? <p>Loading…</p> : null}
          {sentDetail.error ? <div className="error">{sentDetail.error}</div> : null}
          {!sentDetail.loading && !sentDetail.error && sentDetail.people.length === 0 ? (
            <p>The people for this notification were not recorded.</p>
          ) : null}
          {!sentDetail.loading && !sentDetail.error && sentDetail.people.length > 0 ? (
            <ul className="treasurer-sent-people">
              {sentDetail.people.map((person, index) => {
                const seenLabel = person.seen
                  ? (person.seen_at ? `Seen ${person.seen_at}` : 'Seen')
                  : 'Not seen yet';
                return (
                  <li key={`${person.name}-${person.role}-${index}`}>
                    <span>
                      <strong>{person.name}</strong>
                      {(person.role || person.children) ? (
                        <small>{[person.role, person.children].filter(Boolean).join(' · ')}</small>
                      ) : null}
                    </span>
                    <span className="treasurer-sent-marks">
                      {person.channel === 'sms' ? (
                        <FaSms className="treasurer-sent-channel is-sms" title="Sent by SMS" aria-label="Sent by SMS" />
                      ) : null}
                      {person.channel === 'push' ? (
                        <FaMobileAlt className="treasurer-sent-channel is-push" title="Sent by phone" aria-label="Sent by phone" />
                      ) : null}
                      <FaEye
                        className={`treasurer-sent-seen${person.seen ? ' is-seen' : ''}`}
                        title={seenLabel}
                        aria-label={seenLabel}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </div>
    ) : null}
    </div>
  );
};

const AcademyTreasurerReports = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [academies, setAcademies] = useState(() => treasurerAcademies(getCachedAcademies()));
  const [loadingAcademies, setLoadingAcademies] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [view, setView] = useState('balance');
  const [teamFilter, setTeamFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('all');
  const [nameQuery, setNameQuery] = useState('');
  const [ageFilter, setAgeFilter] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);
  const [parentList, setParentList] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityMonth, setActivityMonth] = useState('');
  const [activityKind, setActivityKind] = useState('all');
  const [activityPage, setActivityPage] = useState(1);
  const [statusDetail, setStatusDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadAcademies = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`);
        if (cancelled) {
          return;
        }
        const list = response.data?.success ? (response.data.academies || []) : [];
        setCachedAcademies(list);
        setAcademies(treasurerAcademies(list));
      } catch (err) {
        if (!cancelled) {
          setAcademies(treasurerAcademies(getCachedAcademies()));
        }
      } finally {
        if (!cancelled) {
          setLoadingAcademies(false);
        }
      }
    };
    loadAcademies();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (id || loadingAcademies || academies.length !== 1) {
      return;
    }
    navigate(`/academy-treasurer/${academies[0].id}`, { replace: true });
  }, [id, loadingAcademies, academies, navigate]);

  const loadReport = useCallback(async () => {
    if (!id) {
      setReport(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`${ACADEMY_ADMIN_API}/get_treasurer_report.php?id=${id}`, {
        timeout: 45000,
      });
      const data = response.data;
      if (!data.success) {
        throw new Error(data.message || 'Could not load the treasurer report');
      }
      const nextMonths = (Array.isArray(data.months) ? data.months : [])
        .filter((month) => String(month.year_month || '') <= currentYearMonth());
      setReport({ ...data, months: nextMonths });
      const current = currentYearMonth();
      const preferred = nextMonths.find((month) => month.year_month === current)
        || nextMonths[nextMonths.length - 1];
      setSelectedMonth(preferred?.year_month || null);
    } catch (err) {
      const denied = err.response?.status === 403;
      setError(denied
        ? 'Treasurer access is required'
        : (err.response?.data?.message || err.message || 'Could not load the treasurer report'));
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    if (view !== 'activity' || !id) {
      return undefined;
    }
    let cancelled = false;
    const loadActivity = async () => {
      try {
        setActivityLoading(true);
        setActivityError(null);
        const params = new URLSearchParams({
          id: String(id),
          month: activityMonth,
          kind: activityKind,
          page: String(activityPage),
          per_page: '25',
        });
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_treasurer_activity.php?${params.toString()}`, {
          timeout: 45000,
        });
        if (cancelled) {
          return;
        }
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Could not load activity');
        }
        setActivity(response.data);
      } catch (err) {
        if (!cancelled) {
          setActivityError(err.response?.data?.message || err.message || 'Could not load activity');
          setActivity(null);
        }
      } finally {
        if (!cancelled) {
          setActivityLoading(false);
        }
      }
    };
    loadActivity();
    return () => {
      cancelled = true;
    };
  }, [view, id, activityMonth, activityKind, activityPage]);

  const openSubscription = async (row) => {
    if (!row.subscriptionKind || !id) {
      return;
    }
    setStatusDetail({
      playerName: row.playerName || row.parentName,
      loading: true,
      status: row.subscriptionStatus,
      kind: row.subscriptionKind,
    });
    try {
      const params = new URLSearchParams({
        id: String(id),
        team_id: String(row.teamId),
        player_id: String(row.playerId),
      });
      const response = await api.get(`${ACADEMY_ADMIN_API}/get_treasurer_subscription_detail.php?${params.toString()}`);
      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.message || 'Could not load the subscription');
      }
      setStatusDetail({
        playerName: row.playerName || row.parentName,
        loading: false,
        ...data,
      });
    } catch (err) {
      setStatusDetail({
        playerName: row.playerName || row.parentName,
        loading: false,
        status: row.subscriptionStatus,
        kind: row.subscriptionKind,
        error: err.response?.data?.message || err.message || 'Could not load the subscription',
      });
    }
  };

  useEffect(() => {
    if (!parentList) {
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setParentList(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [parentList]);

  const months = report?.months || [];
  const activeMonth = useMemo(
    () => months.find((month) => month.year_month === selectedMonth) || null,
    [months, selectedMonth]
  );
  const teams = useMemo(
    () => [...(activeMonth?.teams || [])].sort(compareAcademyTeams),
    [activeMonth]
  );
  const ageGroups = useMemo(() => {
    const values = new Set();
    teams.forEach((team) => {
      const age = String(team.age_group || '').trim();
      if (age) {
        values.add(age);
      }
    });
    return [...values].sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
  }, [teams]);
  const scopedTeams = useMemo(
    () => teams.filter((team) => !ageFilter || String(team.age_group || '') === ageFilter),
    [teams, ageFilter]
  );

  const parentRows = useMemo(() => {
    const rows = [];
    teams.forEach((team) => {
      (team.parents || []).forEach((parent) => {
        rows.push({
          key: `${team.team_id}-${parent.player_id}`,
          teamId: String(team.team_id),
          parentName: parent.parent_name || 'No parent',
          playerName: parent.player_name || '',
          ageGroup: team.age_group || '',
          amountPence: parent.amount_pence,
          paid: !!parent.paid,
          isFree: !!parent.is_free,
          playerId: parent.player_id,
          paymentLink: parent.payment_link || '',
          guardians: Array.isArray(parent.guardians) ? parent.guardians : [],
          subscriptionStatus: parent.subscription_status || '',
          subscriptionKind: parent.subscription_kind || '',
        });
      });
    });
    return rows;
  }, [teams]);

  const filteredParentRows = useMemo(() => {
    const query = nameQuery.trim().toLowerCase();
    return parentRows.filter((row) => {
      if (ageFilter && row.ageGroup !== ageFilter) {
        return false;
      }
      if (teamFilter && row.teamId !== teamFilter) {
        return false;
      }
      if (query && !`${row.parentName} ${row.playerName}`.toLowerCase().includes(query)) {
        return false;
      }
      if (paidFilter === 'paid') {
        return row.paid && !row.isFree;
      }
      if (paidFilter === 'unpaid') {
        return !row.paid && !row.isFree;
      }
      return true;
    }).sort(compareParentRows);
  }, [parentRows, teamFilter, paidFilter, nameQuery, ageFilter]);

  const riskRows = useMemo(() => {
    const query = nameQuery.trim().toLowerCase();
    const people = new Map();
    months.forEach((month) => {
      (month.teams || []).forEach((team) => {
        (team.parents || []).forEach((parent) => {
          if (parent.is_free) {
            return;
          }
          const key = `${team.team_id}-${parent.player_id}`;
          if (!people.has(key)) {
            people.set(key, {
              key,
              teamId: String(team.team_id),
              teamName: team.team_name || `Team ${team.team_id}`,
              ageGroup: team.age_group || '',
              parentName: parent.parent_name || 'No parent',
              playerName: parent.player_name || '',
              missed: 0,
              outstandingPence: 0,
              unpaidNow: false,
            });
          }
          if (parent.paid) {
            return;
          }
          const row = people.get(key);
          row.missed += 1;
          row.outstandingPence += Number(parent.amount_pence) || 0;
          if (month.year_month === selectedMonth) {
            row.unpaidNow = true;
          }
        });
      });
    });
    return [...people.values()]
      .filter((row) => row.unpaidNow && row.missed >= 2)
      .filter((row) => !ageFilter || row.ageGroup === ageFilter)
      .filter((row) => !teamFilter || row.teamId === teamFilter)
      .filter((row) => !query || `${row.parentName} ${row.playerName}`.toLowerCase().includes(query))
      .sort((a, b) => b.missed - a.missed || a.parentName.localeCompare(b.parentName, 'en'));
  }, [months, selectedMonth, ageFilter, teamFilter, nameQuery]);

  const cashFor = (teamList) => {
    const byTeam = report?.cash?.by_team || {};
    return teamList.reduce((sum, team) => {
      const row = byTeam[String(team.team_id)] || {};
      sum.last7 += Number(row.last_7_days_pence) || 0;
      sum.monthToDate += Number(row.month_to_date_pence) || 0;
      sum.paid += Number(team.collected_pence) || 0;
      sum.outstanding += Number(team.outstanding_pence) || 0;
      sum.due += Number(team.expected_pence) || 0;
      return sum;
    }, { last7: 0, monthToDate: 0, paid: 0, outstanding: 0, due: 0 });
  };

  const openTeam = (team) => {
    const age = String(team.age_group || '').trim();
    if (ageFilter && age && ageFilter !== age) {
      setAgeFilter('');
    }
    setTeamFilter(String(team.team_id));
    setPaidFilter('all');
    setView('parents');
  };

  const teamsByName = useMemo(
    () => [...scopedTeams].sort(compareTeamNames),
    [scopedTeams]
  );
  const parentTeams = teamFilter
    ? teamsByName.filter((team) => String(team.team_id) === teamFilter)
    : teamsByName;

  const copyPaymentLink = async (row) => {
    if (row.isFree || row.paid || !row.paymentLink) {
      return;
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(row.paymentLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = row.paymentLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!ok) {
          throw new Error('Copy command failed');
        }
      }
      setCopiedKey(row.key);
      showSuccess('Payment link copied');
      window.setTimeout(() => setCopiedKey((current) => (current === row.key ? null : current)), 2000);
    } catch (err) {
      showError('Could not copy the payment link');
    }
  };
  const summary = cashFor(view === 'parents' ? parentTeams : scopedTeams);

  const chooseAcademy = (event) => {
    const nextId = event.target.value;
    if (!nextId) {
      return;
    }
    navigate(`/academy-treasurer/${nextId}`);
  };

  const allowed = Boolean(id) && academies.some((academy) => String(academy.id) === String(id));
  const settingsPath = allowed
    ? academySettingsPathFor(academies, id, authService.isSuperAdmin())
    : null;

  return (
    <div className="membership-players-container academy-payments academy-treasurer container">
      <Header />
      <div className="treasurer-header">
        <h1 className="page-title">Treasurer Reports</h1>
        <div className="treasurer-header-actions">
          {settingsPath ? (
            <Link to={settingsPath} className="treasurer-export">
              <FaCog aria-hidden="true" /> Academy Settings
            </Link>
          ) : null}
          <button type="button" className="treasurer-export">
            Export <FaDownload aria-hidden="true" />
          </button>
        </div>
      </div>

      {!loadingAcademies && academies.length > 1 ? (
        <label className="academy-switcher">
          <span className="visually-hidden">Academy</span>
          <select value={id ? String(id) : ''} onChange={chooseAcademy}>
            <option value="">Choose an academy</option>
            {academies.map((academy) => (
              <option key={academy.id} value={String(academy.id)}>{academy.name}</option>
            ))}
          </select>
        </label>
      ) : null}

      {(loadingAcademies || isLoading) && <div className="loading">Loading report…</div>}

      {!loadingAcademies && !isLoading && academies.length === 0 && (
        <div className="error">Treasurer access is required.</div>
      )}

      {!loadingAcademies && !isLoading && !id && academies.length > 1 && (
        <div className="error">This page address is missing an academy id.</div>
      )}

      {!loadingAcademies && !isLoading && id && !allowed && academies.length > 0 && (
        <div className="error">Treasurer access is required for this academy.</div>
      )}

      {!isLoading && allowed && error && <div className="error">{error}</div>}

      {!isLoading && !error && allowed && report && (
        <>
          <div className="treasurer-views" role="tablist" aria-label="Report view">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'balance'}
              className={view === 'balance' ? 'is-active' : ''}
              onClick={() => setView('balance')}
            >
              Balance
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'parents'}
              className={view === 'parents' ? 'is-active' : ''}
              onClick={() => setView('parents')}
            >
              Teams and parents
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'risk'}
              className={view === 'risk' ? 'is-active' : ''}
              onClick={() => setView('risk')}
            >
              At risk
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'activity'}
              className={view === 'activity' ? 'is-active' : ''}
              onClick={() => setView('activity')}
            >
              Activity
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'notify'}
              className={`treasurer-tab-notify${view === 'notify' ? ' is-active' : ''}`}
              onClick={() => setView('notify')}
            >
              <FaCommentDots aria-hidden="true" />
              Send Notification
            </button>
          </div>

          {view === 'notify' ? (
            <SendNotificationForm
              academyId={id}
              academyName={(academies.find((academy) => String(academy.id) === String(id)) || {}).name || ''}
              month={selectedMonth}
              parents={parentRows}
              parentsReady={!!report}
              showSuccess={showSuccess}
              showError={showError}
            />
          ) : months.length === 0 && view !== 'activity' ? (
            <div className="error">No subscription months found</div>
          ) : (
            <>
          {view !== 'activity' && <div className="treasurer-filters">
            <label>
              Month
              <select
                value={selectedMonth || ''}
                onChange={(event) => setSelectedMonth(event.target.value)}
              >
                {months.map((month) => (
                  <option key={month.year_month} value={month.year_month}>
                    {month.name || month.year_month}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Age group
              <select value={ageFilter} onChange={(event) => setAgeFilter(event.target.value)}>
                <option value="">All age groups</option>
                {ageGroups.map((age) => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </select>
            </label>
            {view !== 'balance' && (
              <label>
                Team
                <select value={teamFilter} onChange={(event) => setTeamFilter(event.target.value)}>
                  <option value="">All teams</option>
                  {(view === 'parents' ? teamsByName : scopedTeams).map((team) => (
                    <option key={team.team_id} value={String(team.team_id)}>{teamLabel(team)}</option>
                  ))}
                </select>
              </label>
            )}
            {view === 'parents' && (
              <label>
                Paid / not paid
                <select value={paidFilter} onChange={(event) => setPaidFilter(event.target.value)}>
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Not paid</option>
                </select>
              </label>
            )}
            {view !== 'balance' && (
              <label className="treasurer-search">
                Parent or child
                <input
                  type="search"
                  value={nameQuery}
                  onChange={(event) => setNameQuery(event.target.value)}
                  placeholder="Search by name"
                />
              </label>
            )}
          </div>}

              {(view === 'balance' || view === 'parents') && (
                <TreasurerBentos
                  last7={summary.last7}
                  monthToDate={summary.monthToDate}
                  paid={summary.paid}
                  outstanding={summary.outstanding}
                  due={summary.due}
                />
              )}

              {view === 'balance' && (
                <div className="treasurer-table-wrap">
                  <table className="treasurer-table">
                    <thead>
                      <tr>
                        <th>Team</th>
                        <th>Age group</th>
                        <th>Received</th>
                        <th>Outstanding</th>
                        <th>Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scopedTeams.map((team) => (
                        <tr key={team.team_id} className="is-clickable" onClick={() => openTeam(team)}>
                          <td>{team.team_name || `Team ${team.team_id}`}</td>
                          <td>{team.age_group || ''}</td>
                          <td>{formatGbp(team.collected_pence)}</td>
                          <td>{formatGbp(team.outstanding_pence)}</td>
                          <td>{formatGbp(team.expected_pence)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {view === 'parents' && (
                filteredParentRows.length === 0 ? (
                  <div className="membership-empty">No parents match these filters.</div>
                ) : (
                  parentTeams.map((team) => {
                    const rows = filteredParentRows.filter((row) => row.teamId === String(team.team_id));
                    if (rows.length === 0) {
                      return null;
                    }
                    return (
                      <section key={team.team_id} className="treasurer-team">
                        {!teamFilter && (
                          <h2 className="treasurer-team-name">{team.team_name || teamLabel(team)}</h2>
                        )}
                        <div className="treasurer-table-wrap">
                          <table className="treasurer-table treasurer-parents-table">
                            <colgroup>
                              <col className="col-name" />
                              <col className="col-age" />
                              <col className="col-amount" />
                              <col className="col-status" />
                              <col className="col-subscription" />
                              <col className="col-link" />
                              <col className="col-more" />
                              <col className="col-access" />
                            </colgroup>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Age group</th>
                                <th>Amount</th>
                                <th>Paid / not paid</th>
                                <th>Status</th>
                                <th>Payment link</th>
                                <th><span className="visually-hidden">Parents</span></th>
                                <th>Access</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row) => {
                                const guardians = row.guardians?.length
                                  ? row.guardians
                                  : (row.parentName && row.parentName !== 'No parent'
                                    ? [{ name: row.parentName, role: '', has_app: false, has_desktop: false }]
                                    : []);
                                const lead = guardians[0] || null;
                                const extra = Math.max(0, guardians.length - 1);
                                const playerLabel = row.playerName || row.parentName;
                                return (
                                <tr key={row.key}>
                                  <td>{playerLabel}</td>
                                  <td>{row.ageGroup}</td>
                                  <td>{row.isFree ? 'Free' : formatGbp(row.amountPence)}</td>
                                  <td className={row.isFree ? '' : (row.paid ? 'is-paid' : 'is-unpaid')}>
                                    {row.isFree ? 'Free' : (row.paid ? 'Paid' : 'Not paid')}
                                  </td>
                                  <td>
                                    {row.subscriptionStatus ? (
                                      <button
                                        type="button"
                                        className={`treasurer-subscription is-${row.subscriptionKind}`}
                                        onClick={() => openSubscription(row)}
                                      >
                                        {row.subscriptionStatus}
                                      </button>
                                    ) : '—'}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="membership-copy"
                                      onClick={() => copyPaymentLink(row)}
                                      disabled={row.isFree || row.paid || !row.paymentLink}
                                      aria-label={
                                        row.paid
                                          ? `${playerLabel} has paid`
                                          : `Copy payment link for ${playerLabel}`
                                      }
                                    >
                                      <FaCopy />
                                      <span>{copiedKey === row.key ? 'Copied' : 'Copy'}</span>
                                    </button>
                                  </td>
                                  <td>
                                    {guardians.length > 0 ? (
                                      <button
                                        type="button"
                                        className="treasurer-more-parents"
                                        onClick={() => setParentList({
                                          childName: row.playerName,
                                          guardians,
                                        })}
                                        aria-label={extra > 0 ? `${extra} more parents for ${playerLabel}` : `Parents for ${playerLabel}`}
                                      >
                                        <FaUser aria-hidden="true" />
                                        {extra > 0 ? <span>+{extra}</span> : null}
                                      </button>
                                    ) : null}
                                  </td>
                                  <td>
                                    <AccessIcons
                                      hasApp={!!lead?.has_app}
                                      hasDesktop={!!lead?.has_desktop}
                                      name={lead?.name || playerLabel}
                                    />
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    );
                  })
                )
              )}

              {view === 'risk' && (
                riskRows.length === 0 ? (
                  <div className="membership-empty">No parents are outstanding across more than one month.</div>
                ) : (
                  <div className="treasurer-table-wrap">
                    <table className="treasurer-table">
                      <thead>
                        <tr>
                          <th>Name (child name)</th>
                          <th>Team</th>
                          <th>Age group</th>
                          <th>Months missed</th>
                          <th>Outstanding</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskRows.map((row) => (
                          <tr key={row.key}>
                            <td>{row.playerName ? `${row.parentName} (${row.playerName})` : row.parentName}</td>
                            <td>{row.teamName}</td>
                            <td>{row.ageGroup}</td>
                            <td>{row.missed}</td>
                            <td>{formatGbp(row.outstandingPence)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
          {view === 'activity' && (
            <ActivityTable
              activity={activity}
              loading={activityLoading}
              error={activityError}
              month={activityMonth}
              kind={activityKind}
              onMonth={(value) => {
                setActivityMonth(value);
                setActivityPage(1);
              }}
              onKind={(value) => {
                setActivityKind(value);
                setActivityPage(1);
              }}
              onPage={setActivityPage}
            />
          )}
        </>
      )}
      {parentList ? (
        <div
          className="modal-overlay"
          onClick={() => setParentList(null)}
        >
          <div
            className="modal treasurer-parents-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="treasurer-parents-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="treasurer-modal-close"
              onClick={() => setParentList(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <h2 id="treasurer-parents-title">
              {parentList.childName ? `Parents for ${parentList.childName}` : 'Parents'}
            </h2>
            <ul className="treasurer-parent-list">
              {parentList.guardians.map((guardian, index) => (
                <li key={`${guardian.name}-${guardian.role}-${index}`}>
                  <AccessIcons
                    hasApp={!!guardian.has_app}
                    hasDesktop={!!guardian.has_desktop}
                    name={guardian.name}
                  />
                  <span>
                    <strong>{guardian.name}</strong>
                    {guardian.role ? <small>{guardian.role}</small> : null}
                    <small>{guardian.last_login ? `Last logged in ${guardian.last_login}` : 'Not logged in'}</small>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {statusDetail ? (
        <div
          className="modal-overlay"
          onClick={() => setStatusDetail(null)}
        >
          <div
            className="modal treasurer-parents-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="treasurer-status-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="treasurer-modal-close"
              onClick={() => setStatusDetail(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <h2 id="treasurer-status-title">{statusDetail.playerName || 'Subscription'}</h2>
            {statusDetail.loading ? <p>Loading…</p> : null}
            {statusDetail.error ? <div className="error">{statusDetail.error}</div> : null}
            {!statusDetail.loading && !statusDetail.error ? (
              <dl className="treasurer-status-detail">
                <div>
                  <dt>Status</dt>
                  <dd>{statusDetail.status || '—'}</dd>
                </div>
                <div>
                  <dt>When paid</dt>
                  <dd>{statusDetail.paid_at || '—'}</dd>
                </div>
                {statusDetail.kind === 'cancelled' ? (
                  <div>
                    <dt>When cancelled</dt>
                    <dd>{statusDetail.cancelled_at || '—'}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Reason</dt>
                  <dd>{statusDetail.reason || '—'}</dd>
                </div>
                {statusDetail.fees ? (
                  <>
                    <div>
                      <dt>Amount</dt>
                      <dd>{formatGbp(statusDetail.fees.amount_pence)}</dd>
                    </div>
                    <div>
                      <dt>Fees deducted</dt>
                      <dd>
                        {formatGbp(Math.max(0, Number(statusDetail.fees.amount_pence) - Number(statusDetail.fees.net_pence)))}
                      </dd>
                    </div>
                    <div>
                      <dt>Reached account</dt>
                      <dd>{formatGbp(statusDetail.fees.net_pence)}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AcademyTreasurerReports;
